import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { aivaApi } from 'src/services';
import { Call, CallPriority, CallStatus, Tag } from 'src/models';
import { NonOptional } from '@insight-health-ai/roseate/utils';

export type GetCallsResponse = {
  calls: Call[];
  page: number;
  page_size: number;
  total: number;
};

export type UseGetCallListParams = {
  product?: 'phone-triage' | 'frontdesk';
  patientId?: string;
  agentId?: string;
  source?: string;
  destination?: string;
  direction?: string;
  startTimeGte?: string;
  startTimeLte?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  tagIds?: string[];
  statusIds?: CallStatus['call_status_id'][];
  priorityIds?: CallPriority['call_priority_id'][];
  isRead?: boolean;
  assignedToPractitionerIds?: string[];
};

const filterToParams = ({
  product,
  patientId,
  agentId,
  source,
  destination,
  direction,
  startTimeGte,
  startTimeLte,
  page,
  pageSize,
  sortBy,
  sortDirection,
  tagIds,
  statusIds,
  priorityIds,
  isRead,
  assignedToPractitionerIds,
}: UseGetCallListParams) => {
  return {
    product,
    patient_id: patientId,
    agent_id: agentId,
    source,
    destination,
    direction,
    start_time_gte: startTimeGte,
    start_time_lte: startTimeLte,
    page,
    page_size: pageSize,
    sort_by: sortBy,
    sort_direction: sortDirection,
    tag_ids: tagIds?.join(','),
    status_ids: statusIds?.join(','),
    priority_ids: priorityIds?.join(','),
    is_read: isRead,
    assigned_to_practitioner_ids: assignedToPractitionerIds?.join(','),
  };
};

const defaultPageSize = 50;

export const useGetCallList = (filters: UseGetCallListParams) => {
  const transformedFilters = filterToParams(filters);
  return useQuery({
    queryKey: ['callsList', filters],
    queryFn: () => {
      return aivaApi
        .get<GetCallsResponse>('/call', {
          params: transformedFilters,
        })
        .then((res) => ({ callList: res.data?.calls, ...res.data }));
    },
  });
};

export const useGetCallInfiniteList = (filters: UseGetCallListParams) => {
  return useInfiniteQuery({
    queryKey: ['callsList', 'infinite', filters],
    queryFn: ({ pageParam }) => {
      return aivaApi
        .get<GetCallsResponse>('/call', {
          params: filterToParams(pageParam),
        })
        .then((res) => ({
          callList: res.data?.calls,
          ...res.data,
        }));
    },
    initialPageParam: {
      page: 1,
      pageSize: defaultPageSize,
      ...filters,
    } as NonOptional<UseGetCallListParams, 'page' | 'pageSize'>,
    getNextPageParam: (lastPage) => {
      return lastPage.page * lastPage.page_size < lastPage.total
        ? { pageSize: defaultPageSize, ...filters, page: lastPage.page + 1 }
        : undefined;
    },
  });
};

export const useCallListClient = () => {
  const queryClient = useQueryClient();

  const scanCall = (
    callId: Call['call_id'],
    replacer: (call: Call) => Call
  ) => {
    queryClient.setQueriesData<ReturnType<typeof useGetCallList>['data']>(
      { queryKey: ['callsList'] },
      (oldData) => {
        if (!oldData?.callList?.length) return oldData;

        return {
          ...oldData,
          callList: oldData.callList.map((call) =>
            call.call_id === callId ? replacer(call) : call
          ),
        };
      }
    );

    queryClient.setQueriesData<
      ReturnType<typeof useGetCallInfiniteList>['data']
    >({ queryKey: ['callsList', 'infinite'] }, (oldData) => {
      if (!oldData?.pages?.length) return oldData;

      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          callList: page.callList.map((call) =>
            call.call_id === callId ? replacer(call) : call
          ),
        })),
      };
    });
  };

  const addTagToCall = ({ callId, tag }: { callId: string; tag: Tag }) => {
    const replacer = (call: Call) => {
      return {
        ...call,
        tags: [...(call.tags || []).filter((t) => t.id !== tag.id), tag],
      };
    };

    scanCall(callId, replacer);
  };

  const removeTagFromCall = ({ callId, tag }: { callId: string; tag: Tag }) => {
    const replacer = (call: Call) => {
      return {
        ...call,
        tags: (call.tags || []).filter((t) => t.id !== tag.id),
      };
    };

    scanCall(callId, replacer);
  };

  const deleteCall = ({ callId }: { callId: Call['call_id'] }) => {
    queryClient.setQueriesData<ReturnType<typeof useGetCallList>['data']>(
      { queryKey: ['callsList'] },
      (oldData) => {
        if (!oldData?.callList?.length) return oldData;

        return {
          ...oldData,
          callList: oldData.callList.filter((call) => call.call_id !== callId),
        };
      }
    );

    queryClient.setQueriesData<
      ReturnType<typeof useGetCallInfiniteList>['data']
    >({ queryKey: ['callsList', 'infinite'] }, (oldData) => {
      if (!oldData?.pages?.length) return oldData;

      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          callList: page.callList.filter((call) => call.call_id !== callId),
        })),
      };
    });
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['callsList'] });
  };

  return {
    addTagToCall,
    removeTagFromCall,
    deleteCall,
    scanCall,
    invalidate,
  };
};
